/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";

import { useState, useEffect } from "react";
import Dropdown from "components/CustomDropdown/Dropdown";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import { getAllEHCs, deleteEHC } from "services/ehcService";
import { toast } from "react-toastify";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import team2 from "assets/images/team-2.jpg";
import PaginationBar from "components/PaginationBar/PaginationBar";

function Tables() {
  const navigate = useNavigate();
  const [testStatus, setTestStatus] = useState("");
  const [ehcData, setEhcData] = useState([]);
  const [allEhcData, setAllEhcData] = useState([]); // Store all data for tab counts
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedEHC, setSelectedEHC] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    pages: 0,
  });

  const handleStatusChange = (event) => {
    setTestStatus(event.target.value);
  };

  // Fetch EHC data
  const fetchEHCData = async () => {
    setLoading(true);
    try {
      // First fetch all data for tab counts
      const allParams = {
        page: 1,
        limit: 100, // Large limit to get all records for counts
      };
      const allResponse = await getAllEHCs(allParams);
      if (allResponse.success) {
        setAllEhcData(allResponse.data);
      }

      // Then fetch paginated data based on current filter
      const params = {
        page: currentPage,
        limit: rowsPerPage,
      };
      if (statusFilter) params.status = statusFilter;

      const response = await getAllEHCs(params);
      if (response.success) {
        setEhcData(response.data);
        setPaginationData({
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching EHC data:", error);
      toast.error(error.message || "Failed to fetch EHC data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEHCData();
  }, [statusFilter, currentPage, rowsPerPage]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Handle Delete
  const handleDeleteClick = (ehc) => {
    setSelectedEHC(ehc);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteEHC(selectedEHC._id);
      if (response.success) {
        toast.success("EHC deleted successfully");
        fetchEHCData(); // Refresh data after delete
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete EHC");
    } finally {
      setDeleteDialog(false);
      setSelectedEHC(null);
    }
  };

  // Author component
  function Author({ image, name, email, id }) {
    return (
      <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
        <ArgonBox mr={2}>
          <ArgonAvatar src={image} alt={name} size="sm" variant="rounded" />
        </ArgonBox>
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {name}
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary">
            {email}
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    );
  }

  // PropTypes validation for Author component
  Author.propTypes = {
    image: PropTypes.string,
    name: PropTypes.string.isRequired,
    email: PropTypes.string,
    id: PropTypes.string,
  };

  // StudentData component
  function StudentData({ student, client }) {
    return (
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          Client-{client}
        </ArgonTypography>
      </ArgonBox>
    );
  }

  // PropTypes validation for StudentData component
  StudentData.propTypes = {
    student: PropTypes.string.isRequired,
    client: PropTypes.string.isRequired,
  };

  // Generate table rows with all columns
  const allRows = ehcData.map((ehc, index) => ({
    "s.no": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {(currentPage - 1) * rowsPerPage + index + 1}
      </ArgonTypography>
    ),
    student: (
      <StudentData
        student={ehc.studentId?.name || "N/A"}
        client={ehc.parentid?.fullName || "N/A"}
      />
    ),
    class: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {ehc.studentId?.class?.name || "N/A"}
      </ArgonTypography>
    ),
    educator: (
      <Author
        image={ehc.tutorId?.profileImage}
        name={ehc.tutorId?.fullName || "N/A"}
        email={ehc.tutorId?.phone || ""}
      />
    ),
    "Invigilator Date & Time": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {new Date(ehc.createdAt).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }) || "N/A"}
      </ArgonTypography>
    ),
    discoveryCall: (
      <ArgonBadge
        variant="gradient"
        badgeContent={ehc.discoverCall?.callStatus || "PENDING"}
        color={
          ehc.discoverCall?.callStatus === "COMPLETED"
            ? "success"
            : ehc.discoverCall?.callStatus === "IN_PROGRESS"
            ? "info"
            : ehc.discoverCall?.callStatus === "SCHEDULED"
            ? "warning"
            : "secondary"
        }
        size="xs"
        container
      />
    ),
    "ehc Time": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {ehc.ehcCoordination?.timeSlot || "N/A"}
      </ArgonTypography>
    ),
    location: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {ehc.parentid?.city || "N/A"}
      </ArgonTypography>
    ),
    "last Ehc Date": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {new Date(ehc.updatedAt || ehc.createdAt).toLocaleDateString()}
      </ArgonTypography>
    ),
    "last Src Date": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {ehc.srcConsultation?.meetingDate
          ? new Date(ehc.srcConsultation.meetingDate).toLocaleDateString()
          : "N/A"}
      </ArgonTypography>
    ),
    "class Status": (
      <ArgonBadge
        variant="gradient"
        badgeContent={ehc.studentId?.hasActiveClass ? "Active" : "Inactive"}
        color={ehc.studentId?.hasActiveClass ? "success" : "secondary"}
        size="xs"
        container
      />
    ),
    "ehcTest Status": (
      <ArgonBadge
        variant="gradient"
        badgeContent={ehc.status}
        color={
          ehc.status === "COMPLETED" ? "success" : ehc.status === "REVIEWED" ? "info" : "warning"
        }
        size="xs"
        container
      />
    ),
    action: (
      <ArgonBox display="flex" gap={1}>
        <Button
          variant="text"
          color="info"
          size="small"
          onClick={() => navigate(`/activities/ehc/view/${ehc._id}`)}
        >
          <VisibilityIcon fontSize="small" />
        </Button>
        <Button
          variant="text"
          color="warning"
          size="small"
          onClick={() => navigate(`/activities/ehc/edit/${ehc._id}`, { state: { ehcData: ehc } })}
        >
          <EditIcon fontSize="small" />
        </Button>
        <Button variant="text" color="error" size="small" onClick={() => handleDeleteClick(ehc)}>
          <DeleteIcon fontSize="small" />
        </Button>
      </ArgonBox>
    ),
  }));

  const columns = [
    { name: "s.no", align: "center" },
    { name: "student", align: "left" },
    { name: "class", align: "center" },
    { name: "educator", align: "left" },
    { name: "Invigilator Date & Time", align: "left" },
    { name: "discoveryCall", align: "center" },
    { name: "ehc Time", align: "center" },
    { name: "location", align: "center" },
    { name: "last Ehc Date", align: "center" },
    { name: "last Src Date", align: "center" },
    { name: "class Status", align: "center" },
    { name: "ehcTest Status", align: "center" },
    { name: "action", align: "center" },
  ];

  const tabsData = [
    { label: "All", count: allEhcData.length, color: "#2027e1ff" },
    {
      label: "Draft",
      count: allEhcData.filter((e) => e.status === "DRAFT").length,
      color: "#ec3c10ff",
    },
    {
      label: "Completed",
      count: allEhcData.filter((e) => e.status === "COMPLETED").length,
      color: "#10a6ecff",
    },
    {
      label: "Reviewed",
      count: allEhcData.filter((e) => e.status === "REVIEWED").length,
      color: "#1a6808b7",
    },
  ];

  // Handle tab change
  const handleTabChange = (tabLabel) => {
    if (tabLabel === "All") {
      setStatusFilter("");
    } else {
      setStatusFilter(tabLabel.toUpperCase());
    }
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  // Get active tab based on current statusFilter
  const getActiveTab = () => {
    if (!statusFilter) return "All";
    return statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1).toLowerCase();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  All EHC Reports
                </ArgonTypography>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => navigate("/activities/ehc/add")}
                >
                  + Add EHC
                </ArgonButton>
              </ArgonBox>

              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs
                    tabsData={tabsData}
                    onTabChange={handleTabChange}
                    activeTab={getActiveTab()}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body2">Loading...</ArgonTypography>
                </ArgonBox>
              ) : allRows.length > 0 ? (
                <Table columns={columns} rows={allRows} />
              ) : (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body2">No EHC records found</ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {allRows.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={paginationData.total}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this EHC report for {selectedEHC?.studentId?.fullName}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
